import { EdgeLabelRenderer, type EdgeProps } from '@xyflow/react';
import { FC } from 'react';

import { roundedPolylinePath, type Point } from './canvas-edge-routing';
import type { CanvasEdgeKind } from './canvas-edges-model';

/**
 * - `none`: drawn at full strength.
 * - `select`: not connected to the selected agent (.18 wire, .3 label).
 * - `trace`: another edge's label is being hovered (.07 wire, .1 label).
 */
export type WireDim = 'none' | 'select' | 'trace';

export interface RoutedEdgeData extends Record<string, unknown> {
  kind: CanvasEdgeKind;
  pts: Point[];
  end: Point;
  mid: Point;
  /** Where the label pill sits — see `RoutedWire.label`. */
  labelAt: Point;
  labelAlign: 'center' | 'before' | 'after';
  label?: string;
  /** The label's tooltip, e.g. "Supervisor Agent → Billing". */
  title?: string;
  showLabel: boolean;
  dim: WireDim;
  /** This edge is the one being traced. */
  hot: boolean;
  onTrace?: (edgeId: string | null) => void;
}

/**
 * The Build canvas's one edge: an orthogonal, round-cornered polyline routed
 * by `canvas-edge-routing.ts`, a 3px dot where it lands (no arrowhead), and a
 * label pill on its last leg, beside the card it reaches (or its midpoint
 * when that leg is short). Matches the prototype's `.cv-wire`,
 * `.cv-dot` and `.cv-label` (`prostudio-build-flow-layouts.html`). React
 * Flow's own `sourceX`/`targetX` are ignored — the route is computed from the
 * whole layout, which one edge on its own cannot see.
 */
export const RoutedEdge: FC<EdgeProps> = ({ id, data }) => {
  const {
    kind,
    pts,
    end,
    labelAt,
    labelAlign,
    label,
    title,
    showLabel,
    dim,
    hot,
    onTrace,
  } = data as unknown as RoutedEdgeData;
  // Hung before the point (the line arrives from the left), after it, or
  // centred on it.
  const shiftX =
    labelAlign === 'before' ? '-100%' : labelAlign === 'after' ? '0' : '-50%';

  const state = [
    dim === 'select' && 'is-dim',
    dim === 'trace' && 'is-trace-dim',
    hot && 'is-hot',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <path
        className={`c-canvas-wire c-canvas-wire--${kind} ${state}`}
        d={roundedPolylinePath(pts)}
        data-testid="bobcat-canvas-wire"
      />
      <circle
        className={`c-canvas-wire-dot c-canvas-wire--${kind} ${state}`}
        cx={end[0]}
        cy={end[1]}
        r={3}
      />
      {showLabel && label && (
        <EdgeLabelRenderer>
          <div
            className={`c-canvas-label c-canvas-label--${kind} ${state} nodrag nopan`}
            style={{
              transform: `translate(${shiftX}, -50%) translate(${labelAt[0]}px, ${labelAt[1]}px)`,
            }}
            title={title}
            data-testid="bobcat-canvas-edge-label"
            onMouseEnter={() => onTrace?.(id)}
            onMouseLeave={() => onTrace?.(null)}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};
